import React, {useCallback, useEffect, useState} from 'react'
import {
  Alerts,
  ErrorLoadingRetry,
  FormBuilderFormRenderer,
  RPCs,
  useReportEntry,
  useReportEntryType,
  useRPCContext,
} from '@therms/react-modules'
import {Button, Loading} from '@therms/atalaya'
import * as Yup from 'yup'
import {Formik} from 'formik'
import {FormikValuesChangeListener} from '@src/components/FormikValuesChangeListener'
import {Prompt} from 'react-router-dom'

const ValidationSchema = Yup.object().shape({})

interface SingleEntryFormProps {
  entryId?: string
  entryTypeId: string
  onFormBuilderValidate?: (errors?: {[key: string]: string}) => void
  onSaved?: () => void
  onUnsavedChanges?: (hasUnsavedChanges: boolean) => void
  reportId: string
}

function SingleEntryForm({
  entryId,
  entryTypeId,
  onFormBuilderValidate,
  onSaved,
  onUnsavedChanges,
  reportId,
}: SingleEntryFormProps) {
  const [saving, setSaving] = useState(false)
  const [showSave, setShowSave] = useState(false)

  const {rpcClient} = useRPCContext()

  const {data: entryData} = useReportEntry(
    entryId ? {entryId, reportId} : undefined,
  )

  const {
    data: entryTypeData,
    loading: entryTypeLoading,
    refresh: refreshEntryType,
  } = useReportEntryType(
    // the `entryData` has the entryType if we're editing an existing entryId
    !entryId && entryTypeId ? {entryTypeId} : undefined,
  )

  const handleDirtyChange = useCallback(
    (hasUnsavedChanges: boolean) => {
      setShowSave(hasUnsavedChanges)
      onUnsavedChanges?.(hasUnsavedChanges)
    },
    [onUnsavedChanges],
  )

  useEffect(() => {
    return () => {
      onUnsavedChanges?.(false)
    }
  }, [onUnsavedChanges])

  const save = useCallback(
    async (values) => {
      setSaving(true)

      try {
        await rpcClient.call(
          entryId
            ? RPCs.Reports.UpdateReportEntry.v2
            : RPCs.Reports.CreateReportEntry.v2,
          {
            entry: values,
            reportId,
          },
        )

        Alerts.success(`Report saved`)

        return true
      } catch (err: any) {
        Alerts.error('There was a problem saving the Report', err.message)

        console.log('save() err', err)

        return false
      } finally {
        setSaving(false)
      }
    },
    [entryId, reportId, rpcClient],
  )

  const formId =
    entryData?.entry?.formInput?.formId || entryTypeData?.entryType?.formId

  if (!formId) {
    return (
      <div className="h-20 w-full text-center">
        <Loading message="Loading Report" messagePosition="vertical" />
      </div>
    )
  }

  if (!entryTypeLoading && !entryData && !entryTypeData)
    return (
      <div className="h-20 w-full">
        <ErrorLoadingRetry onRetry={refreshEntryType} />
      </div>
    )

  return (
    <div className="relative">
      <Prompt
        when={showSave}
        message="You have unsaved changes, are you sure you want to navigate away?"
      />
      <Formik
        enableReinitialize
        initialValues={
          entryData?.entry || {entryTypeId, formInput: {fields: {}}}
        }
        onSubmit={async (values, {resetForm}) => {
          const saved = await save(values)

          if (saved) {
            resetForm({values})
            handleDirtyChange(false)
            onSaved?.()
          }
        }}
        validationSchema={ValidationSchema}
      >
        {({setFieldValue, submitForm, values}) => (
          <div>
            <FormikValuesChangeListener
              debounceMs={0}
              onValuesDiffChange={handleDirtyChange}
            />

            {saving && <Loading message="Saving Entry" overlay />}

            {showSave && (
              <div className="flex justify-end">
                <Button onClick={submitForm} variant="main">
                  Save Changes
                </Button>
              </div>
            )}

            <FormBuilderFormRenderer
              formId={formId}
              formValues={values.formInput.fields}
              onFormErrorStateChange={onFormBuilderValidate}
              initialValue={entryData?.entry?.formInput.fields}
              onFormInputChange={(_values) => {
                setFieldValue('formInput', _values)
              }}
            />

            {showSave && (
              <div className="flex justify-end">
                <Button onClick={submitForm} variant="main">
                  Save Changes
                </Button>
              </div>
            )}
          </div>
        )}
      </Formik>
    </div>
  )
}

export {SingleEntryForm}
